// Helpers for rendering a creative the way the Way2News app shows it, so a
// downloaded preview matches what a reader actually sees: a full image ad
// centred on a black screen under an "Ad" badge, a promoted story as a feed
// card with the sponsored label and action bar beneath it.

import { franc } from "franc-min";

// The frame is laid out at the phone's density independent size and captured
// at the scale that reproduces a real device screenshot, so a download is
// pixel for pixel what ops sees when they screenshot the app.
//
//   390 x 867 dp  x  1.477  =  576 x 1280 px  (390 wide like the generator)
//
// Keep FRAME_WIDTH in step with the .ss-frame width in screenshotFrame.scss.
export const FRAME_WIDTH = 390;
export const FRAME_HEIGHT = 867;
export const CAPTURE_SCALE = 1.477;

// lang_id -> template language, mirroring langMap in screenshot-api.js.
export const frameLangMap = {
  1: "telugu",
  2: "tamil",
  3: "hindi",
  4: "kannada",
  5: "malayalam",
  6: "marathi",
  7: "bengali",
  8: "gujarati",
  10: "english",
  11: "english",
  13: "english",
};

export const DEFAULT_FRAME_LANG = "telugu";

// Only the languages the frame can actually typeset, in the order ops expect.
export const frameLanguageOptions = [
  { value: "telugu", label: "Telugu" },
  { value: "tamil", label: "Tamil" },
  { value: "hindi", label: "Hindi" },
  { value: "kannada", label: "Kannada" },
  { value: "malayalam", label: "Malayalam" },
  { value: "marathi", label: "Marathi" },
  { value: "bengali", label: "Bengali" },
  { value: "gujarati", label: "Gujarati" },
  { value: "english", label: "English" },
];

// Creative sub types that read as a full screen article card, and so can be
// framed. Anything else keeps the plain drawer preview.
export const FRAMEABLE_SUB_TYPES = [
  "full_image",
  "gallery",
  "promoted_image_story",
  "promoted_gallery",
  "rec_image",
  "rec_promoted_story",
  "full_video",
  "fullvideo_or_gif",
  "video_with_bg",
  "half_video",
  "promoted_video_story",
  "video_gallery",
];

export const isFrameable = (preview) =>
  !!preview && FRAMEABLE_SUB_TYPES.includes(preview.sub_type);

// Sub types that carry a headline/description and so get the article body,
// timestamp and sponsored label under the banner.
const STORY_SUB_TYPES = [
  "promoted_image_story",
  "promoted_gallery",
  "rec_promoted_story",
  "promoted_video_story",
];

// Video creatives are drawn as their thumbnail under a play button, which is
// how the app shows them before playback: html-to-image cannot capture an
// iframe or <video>. fullvideo_or_gif autoplays in the app, so it gets none.
const PLAY_SUB_TYPES = [
  "full_video",
  "video_gallery",
  "video_with_bg",
  "half_video",
  "promoted_video_story",
];

// half_video lays a video band over a background image; `position` places it.
const HALF_BAND_ALIGN = { 0: "start", 1: "center", 2: "end", Top: "start", Middle: "center", Bottom: "end" };

// YouTube embed links (also the odd /mp4/embed/ form) carry the video id;
// i.ytimg.com serves its thumbnail with CORS open, unlike most ad hosts.
const youtubeThumb = (videoUrl) => {
  const id = /embed\/([\w-]{6,})/.exec(videoUrl || "")?.[1];
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : "";
};

// Unicode blocks are unambiguous for these scripts, which makes them a far
// better signal than franc on the short strings creatives carry. Devanagari is
// shared by Hindi and Marathi, so that one case falls through to franc.
const SCRIPT_RANGES = [
  { language: "telugu", re: /[ఀ-౿]/ },
  { language: "tamil", re: /[஀-௿]/ },
  { language: "kannada", re: /[ಀ-೿]/ },
  { language: "malayalam", re: /[ഀ-ൿ]/ },
  { language: "bengali", re: /[ঀ-৿]/ },
  { language: "gujarati", re: /[઀-૿]/ },
];

const DEVANAGARI = /[ऀ-ॿ]/;

/**
 * Best guess at the language of a creative, used to pick the frame's fonts.
 * Callers let the user override the result.
 */
export const detectFrameLanguage = (preview) => {
  if (!preview) return DEFAULT_FRAME_LANG;

  const text = `${preview.head_line || ""} ${preview.title || ""} ${
    preview.description || ""
  }`.trim();

  if (text) {
    const match = SCRIPT_RANGES.find(({ re }) => re.test(text));
    if (match) return match.language;

    if (DEVANAGARI.test(text)) {
      // franc-min separates the two Devanagari languages; it needs a little
      // text to be meaningful, so fall back to Hindi on short strings.
      return franc(text) === "mar" ? "marathi" : "hindi";
    }

    if (/[A-Za-z]/.test(text)) return "english";
  }

  // Image-only creatives carry no text. The language picked for the button
  // text is the only other hint the record holds.
  if (preview.btn_text_lang) {
    return frameLangMap[preview.btn_text_lang] || DEFAULT_FRAME_LANG;
  }

  return DEFAULT_FRAME_LANG;
};

// Creative copy stores links, phone numbers and sub text as <<target>>label<<>>.
// Only the label is shown.
export const stripCreativeMarkup = (value) =>
  (value || "").replace(/<<.*?>>(.*?)<<>>/g, "$1").trim();

/**
 * Split creative copy into runs, flagging the ones that were wrapped in
 * <<target>>...<<>>. The app paints those blue, so the frame does too.
 *
 * @returns {Array<{text: string, link: boolean}>}
 */
export const toCreativeSegments = (value) => {
  const text = (value || "").trim();
  if (!text) return [];

  const segments = [];
  const pattern = /<<(.*?)>>(.*?)<<>>/g;
  let cursor = 0;
  let match = pattern.exec(text);

  while (match !== null) {
    if (match.index > cursor) {
      segments.push({ text: text.slice(cursor, match.index), link: false });
    }
    segments.push({ text: match[2], link: true });
    cursor = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), link: false });
  }

  return segments.filter((segment) => segment.text);
};

const toImageList = (value) => {
  if (!value) return [];
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((entry) => (entry && entry.image ? entry.image : entry))
    .filter((url) => typeof url === "string" && url);
};

/**
 * Turn a creative into the frames to render. Galleries produce one frame per
 * image; everything else produces a single frame.
 *
 * @returns {Array<{kind, imgUrl, videoThumb, bandAlign, showPlay, headline,
 *   bodySegments, isStory, button}>}
 */
export const buildFrames = (preview) => {
  if (!preview) return [];

  const isVideo = PLAY_SUB_TYPES.includes(preview.sub_type);
  const isHalf = preview.sub_type === "half_video";
  const videoThumb = youtubeThumb(preview.video_urls);

  // A video gallery holds an array of video links and no images of its own, so
  // each video becomes a frame on its YouTube thumbnail, or on black.
  let images =
    preview.sub_type === "video_gallery"
      ? (Array.isArray(preview.video_urls) ? preview.video_urls : []).map(
          (url) => youtubeThumb(url)
        )
      : toImageList(preview.gallery || preview.img_urls);
  // A video without its own thumbnail falls back to the YouTube one; with
  // neither, the frame still renders, on black.
  if (!images.length && isVideo && !isHalf) {
    images = [videoThumb || ""];
  }
  if (!images.length && !isHalf) return [];
  if (isHalf) images = [images[0] || ""];

  const isStory = STORY_SUB_TYPES.includes(preview.sub_type);

  const button =
    preview.button_enabled && preview.button_text
      ? {
          text: preview.button_text,
          color: preview.btn_text_clr || "#ffffff",
          background: preview.btn_text_bgclr || "#2563eb",
        }
      : null;

  return images.map((imgUrl, index) => ({
    kind: isHalf ? "half" : isStory ? "card" : "screen",
    imgUrl,
    videoThumb: isHalf ? videoThumb : "",
    bandAlign: HALF_BAND_ALIGN[preview.position] || "start",
    showPlay: isVideo,
    // Copy belongs to the creative as a whole, so it rides on the first frame
    // of a gallery only.
    headline: isStory && index === 0
      ? stripCreativeMarkup(preview.head_line || preview.title)
      : "",
    bodySegments:
      isStory && index === 0 ? toCreativeSegments(preview.description) : [],
    isStory,
    button: index === 0 ? button : null,
  }));
};

// Filenames end up on the user's disk, so drop anything a filesystem dislikes.
export const toSafeFileName = (name) =>
  (name || "creative")
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 120);
